import { AxiosInstance, AxiosResponse } from "axios"
import JSZip from "jszip"
import { fileToBase64 } from "utils/files/fileToBase64"
import type { RcFile } from "antd/es/upload/interface"
import type { DocumentApiItem } from "types/document.types"
/**
 * Documents API functions - Updated to support folder structure
 * Authentication headers are handled automatically by the axios instance
 */

/**
 * Uploads a document to a specific folder.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param project_id - The project ID.
 * @param hint_owner - The owner of the project.
 * @param filename - The name of the file to be uploaded.
 * @param file - The file object to be uploaded.
 * @param folder - The folder path where the file should be uploaded (optional).
 * @param overwrite - Whether to overwrite existing files (optional).
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function documentUpload(
  axiosInstance: AxiosInstance,
  project_id: string,
  hint_owner: string,
  filename: string,
  file: File | Blob | RcFile,
  folder: string = "",
  overwrite: boolean = true
): Promise<AxiosResponse<unknown>> {
  // Initialize JSZip instance
  const zip = new JSZip()

  // Add file content to zip
  zip.file(filename, file, { compression: "DEFLATE" })

  const zippedContent: Blob = await zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
  })

  const zippedBase64: string = await fileToBase64(zippedContent)

  const encoder = new TextEncoder()
  const encoded: Uint8Array = encoder.encode(zippedBase64)

  // Build query parameters
  const params = new URLSearchParams({
    id: project_id,
    hint_owner,
    name: filename,
    encode: "base64_zip",
    overwrite: overwrite.toString(),
  })

  // Add folder parameter if provided
  if (folder && folder.trim() !== "") {
    params.append("folder", folder)
  }

  // Replace + with %20 to preserve spaces in filenames (URLSearchParams uses form encoding)
  const queryString = params.toString().replace(/\+/g, "%20")

  const response = await axiosInstance.post(
    `/document-upload?${queryString}`,
    encoded
  )
  return response
}

/**
 * Delete a document or folder.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param project_id - The project ID.
 * @param hint_owner - The owner of the project.
 * @param name - The name/path of the file or folder to be deleted.
 * @param is_folder - Whether the item to delete is a folder.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function documentDelete(
  axiosInstance: AxiosInstance,
  project_id: string,
  hint_owner: string,
  name: string,
  is_folder: boolean = false
): Promise<AxiosResponse<unknown>> {
  const params = new URLSearchParams({
    id: project_id,
    hint_owner,
    name,
    is_folder: is_folder.toString(),
  })

  // Replace + with %20 to preserve spaces in filenames
  const queryString = params.toString().replace(/\+/g, "%20")

  const response = await axiosInstance.delete(
    `/document-delete?${queryString}`
  )
  return response
}

/**
 * Fetch a document's content without downloading it.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param project_id - The project ID.
 * @param hint_owner - The owner of the project.
 * @param name - The name/path of the file to be fetched.
 * @returns The document's content and the file name it is stored under.
 * @throws Will throw an error if the request fails.
 */
export async function documentFetch(
  axiosInstance: AxiosInstance,
  project_id: string,
  hint_owner: string,
  name: string
): Promise<{ blob: Blob; filename: string }> {
  const config = {
    responseType: "text" as const, // Expecting base64 encoded string
  }

  const params = new URLSearchParams({
    id: project_id,
    hint_owner,
    name,
    encode: "base64_zip",
  })

  // Replace + with %20 to preserve spaces in filenames
  const queryString = params.toString().replace(/\+/g, "%20")

  // Fetch the base64 encoded zip content from the API
  const response = await axiosInstance.get(
    `/document-download?${queryString}`,
    config
  )

  const base64Data: string = response.data.trim() // Get the base64-encoded string from the server

  // 1. Decode the base64 string to a binary string
  const binaryString: string = window.atob(base64Data) // This decodes the base64 string into a binary string

  // 2. Convert the binary string to Uint8Array (JSZip expects binary data)
  const len: number = binaryString.length
  const uint8Array = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    uint8Array[i] = binaryString.charCodeAt(i) // This step converts the binary string into a Uint8Array
  }

  // Initialize JSZip and load the binary data
  const zip = new JSZip()
  const zipContent = await zip.loadAsync(uint8Array) // Load the binary data into JSZip

  // Get the first file in the zip archive (assuming single file)
  const fileNames = Object.keys(zipContent.files)
  if (fileNames.length === 0) {
    throw new Error("No files found in the zip archive")
  }

  const unzippedFile = fileNames[0]
  const zipFile = zip.file(unzippedFile)

  if (!zipFile) {
    throw new Error("Could not extract file from zip")
  }

  const blob: Blob = await zipFile.async("blob")
  return { blob, filename: unzippedFile }
}

/**
 * Download a document.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param project_id - The project ID.
 * @param hint_owner - The owner of the project.
 * @param name - The name/path of the file to be downloaded.
 * @returns Resolves when the download is triggered.
 * @throws Will throw an error if the request fails.
 */
export async function documentDownload(
  axiosInstance: AxiosInstance,
  project_id: string,
  hint_owner: string,
  name: string
): Promise<void> {
  try {
    const { blob: unzippedData, filename: unzippedFile } = await documentFetch(
      axiosInstance,
      project_id,
      hint_owner,
      name
    )

    // Create a link element to download the unzipped file
    const url: string = window.URL.createObjectURL(unzippedData)
    const link: HTMLAnchorElement = document.createElement("a")
    link.href = url
    link.setAttribute("download", unzippedFile) // Set the filename for download

    // Append the link to the body
    document.body.appendChild(link)
    link.click()
    window.URL.revokeObjectURL(url)
    link.remove()
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred"
    console.error("Error in downloading or unzipping document:", errorMessage)
    throw error
  }
}

/**
 * Retrieves the list of documents for a given project with folder support.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param project_id - The project ID.
 * @param hint_owner - The owner of the project.
 * @param find - The search query to filter the documents.
 * @param folder - The folder path to list documents from (optional).
 * @returns The response from the API containing the list of documents as JSON array.
 * @throws Will throw an error if the request fails.
 */
export async function Availabledocuments(
  axiosInstance: AxiosInstance,
  project_id: string,
  hint_owner: string,
  find: string = "",
  folder: string = ""
): Promise<AxiosResponse<DocumentApiItem[]>> {
  const params = new URLSearchParams({
    id: project_id,
    hint_owner,
  })

  // Add optional parameters
  if (find && find.trim() !== "") {
    params.append("find", find)
  }
  if (folder && folder.trim() !== "") {
    params.append("folder", folder)
  }

  // Replace + with %20 to preserve spaces in folder/search terms
  const queryString = params.toString().replace(/\+/g, "%20")

  const response = await axiosInstance.get(
    `/available-documents?${queryString}`
  )
  return response
}

/**
 * Creates a new folder in the project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param project_id - The project ID.
 * @param hint_owner - The owner of the project.
 * @param folderPath - The path of the folder to create.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function documentFolderCreate(
  axiosInstance: AxiosInstance,
  project_id: string,
  hint_owner: string,
  folderPath: string
): Promise<AxiosResponse<unknown>> {
  const params = new URLSearchParams({
    id: project_id,
    hint_owner,
    folder: folderPath,
  })

  // Replace + with %20 to preserve spaces in folder paths
  const queryString = params.toString().replace(/\+/g, "%20")

  const response = await axiosInstance.post(
    `/document-folder-create?${queryString}`
  )
  return response
}

/**
 * Deletes a folder and all its contents from the project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param project_id - The project ID.
 * @param hint_owner - The owner of the project.
 * @param folderPath - The path of the folder to delete.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function documentFolderDelete(
  axiosInstance: AxiosInstance,
  project_id: string,
  hint_owner: string,
  folderPath: string
): Promise<AxiosResponse<unknown>> {
  const params = new URLSearchParams({
    id: project_id,
    hint_owner,
    folder: folderPath,
  })

  // Replace + with %20 to preserve spaces in folder paths
  const queryString = params.toString().replace(/\+/g, "%20")

  const response = await axiosInstance.delete(
    `/document-folder-delete?${queryString}`
  )
  return response
}
